<template>
  <div class="opc-delta-edit-container">
    <h3 class="opc-zm-base-card__main__sub__title">{{ subTitle || $t('billing.delta.buy_more_licenses') }}</h3>
    <bought-license :type="planType" :custom-unit="unit" showExtraInfo :extraInfo="$t('billing.delta.get_more_licenses')" />
    <div class="opc-zm-base-card__main__content">
      <div class="opc-zm-base-card__main__content-left">
        <InputNumberWithPopover
          v-model="deltaForm.selectedNumber"
          class="opc-quantity-selector"
          size="lg"
          controls-position="both"
          :min="1"
          :max="maxToAdd || 99"
          :label="`${trackingPlanName} Select the number of license(s)`"
          :data-testid="`checkout-input-license-${currentPlan.plan_code}`"
        />
        <span class="opc-modal-tips-title">
          {{ $t('billing.delta.new_licenses_desc', deltaForm.selectedNumber) }}
        </span>
      </div>
      <span class="opc-modal-billing-cycle">
        <span class="opc-modal-billing-cycle-price">
          {{ currentBillingCyclePrice }}
        </span>
        <span class="opc-modal-cycle-info" v-html="billingText">
        </span>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { throttle } from 'lodash';
import { planUnitMap } from '@/common/product-unit';
import { planTypes } from '@/common/constants';
import { getBillTextByCycle } from '@/common/product-unit';
import { getEffectiveAmount } from '@/utils/downsell-price';

import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import discountedMonthPrice from '@/common/mixin/discounted-month-price';
import { formatPriceWithPriceProperties } from '@/utils/money';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  name: 'DeltaEditMian',
  mixins: [discountedMonthPrice],
  data() {
    return {
      deltaForm: {
        selectedNumber: 1,
      },
    };
  },
  props: {
    planType: {
      type: String,
      require: true,
    },
    title: {
      type: String,
      require: true,
    },
    subTitle: {
      type: String,
      require: false,
    },
    maxToAdd: {
      type: Number,
      require: false,
    },
    unit: {
      type: String,
      require: false,
    },
  },
  components: {
    BoughtLicense,
    InputNumberWithPopover,
  },
  mounted() {
    const currentBoughtPlanSelectedNumber = this.currentBoughtPlan?.cartInfo?.selected_number || 1;
    const currentSelectedPlan = this.getSelectedPlans.find(p => p.type === this.planType);
    const currentSelectedPlanSelectedNumber = currentSelectedPlan?.cartInfo?.selected_number;
    const addQty = currentSelectedPlanSelectedNumber - currentBoughtPlanSelectedNumber;
    this.deltaForm.selectedNumber = Number(addQty) > 0 ? Number(addQty) : 1;
  },
  computed: {
    ...mapGetters(["boughtPlans", "currentEditPlan", "getSelectedPlans"]),
    currentPlan() {
      const currentSelectedPlan = this.getSelectedPlans.find(p => p.type === this.planType);
      if (currentSelectedPlan?.plan_code && this.boughtPlans?.find((item) => item.plan_code === currentSelectedPlan.plan_code)) {
        return currentSelectedPlan;
      }
      return this.currentEditPlan;
    },
    currentBoughtPlan() {
      return this.boughtPlans?.find((item) => item.plan_code === this.currentPlan.plan_code) || {};
    },
    trackingPlanKey() {
      return getTrackingProductNameByPlanType(this.planType);
    },
    trackingPlanName() {
      if (this.planType === planTypes.LARGE_MEETING) {
        return `${this.trackingPlanKey} ${this.currentPlan.capacity} participants`;
      }
      return `${this.trackingPlanKey} `;
    },
    currentBillingCyclePrice() {
      const info = this.getPriceOfMonthWithDownsell(this.currentPlan);
      return formatPriceWithPriceProperties(info);
    },
    billingText() {
      const amount = getEffectiveAmount(this.currentPlan);
      return this.getMergedBilledText(this.currentPlan.billing_cycle, amount, planUnitMap.license);
    },
  },
  watch: {
    'deltaForm.selectedNumber': {
      handler() {
        this.$nextTick(() => {
          const selectedNumber =
            (this.currentBoughtPlan?.cartInfo?.selected_number ?? 0) + this.deltaForm.selectedNumber;
          this.submitForm(selectedNumber);
        });
      },
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    submitForm: throttle(function (v) {
      this.$nextTick(() => {
        this.updateSelectPlan({
          plan_code: this.currentPlan.plan_code,
          cartInfo: { selected_number: v },
        });
      });
    }, 200),
    getBillTextByCycle(billingCycle) {
      return getBillTextByCycle(billingCycle);
    },
  }
}
</script>

<style lang="scss" scoped>
.opc-delta-edit-container {
  .opc-zm-base-card__main {
    &__sub__title {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-size: 18px;
      font-weight: 700;
      line-height: 150%;
      margin-bottom: 4px;
    }

    &__content {
      display: flex;
      margin-top: 12px;
      align-items: flex-start;
      justify-content: space-between;

      &-left {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
      }

      .opc-modal-tips-title {
        font-size: 14px;
        font-weight: 400;
        margin: 0 8px;
        flex-shrink: 0;
      }
      .opc-modal-billing-cycle {
        margin-left: 12px;
        display: inline-flex;
        flex-wrap: wrap;
        column-gap: 6px;
        align-items: baseline;
        padding-top: 8px;

        .opc-modal-billing-cycle-price {
          color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
          font-size: 20px;
          font-weight: 600;
          line-height: 24px; /* 120% */
          letter-spacing: -0.45px;
        }
        .opc-modal-cycle-info {
          color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
          font-size: 14px;
          font-weight: 400;
          line-height: 18px; /* 128.571% */
          letter-spacing: -0.15px;
        }
      }
    }
  }
}
</style>
